// Copied and adapted from https://github.com/yuanqing/shared-event-listeners
// The project was archived, so I didn't want to risk adding as a package
// for it to be later deleted from NPM.

type Listener = (event: Event | KeyboardEvent) => void;
type Remove = () => void;
type Add = (eventName: string, listener: Listener) => Remove;
type MapEventNameToListeners = Record<string, Listener[]>;
type MapEventNameToSharedListener = Record<string, Listener>;

interface SharedEventListener {
	mapEventNameToListeners: MapEventNameToListeners;
	mapEventNameToSharedListener: MapEventNameToSharedListener;
	add: Add;
	remove: Remove;
}

const mapWindowEventNameToListeners = {};
const mapWindowEventNameToSharedListener = {};

export default (element: Element | Window = window): SharedEventListener => {
	// If the `element` is `window`, persist `mapEventNameToListeners` and
	// `mapEventNameToSharedListener` across different calls
	// to `sharedEventListeners`.
	const isWindow = element === window;

	// Maps `eventName` to the list of listeners associated with each
	// respective `eventName`.
	const mapEventNameToListeners: MapEventNameToListeners = isWindow
		? mapWindowEventNameToListeners
		: {};

	// Maps `eventName` to the shared listener bound to `element`.
	const mapEventNameToSharedListener: MapEventNameToSharedListener = isWindow
		? mapWindowEventNameToSharedListener
		: {};

	// Remove the `listener` from the list of listeners associated with
	// the `eventName`.
	function remove(eventName?: string, listener?: Listener) {
		const listeners = mapEventNameToListeners[eventName as string];
		const listenerIndex = listeners
			? listeners.indexOf(listener as Listener)
			: -1;
		if (listenerIndex === -1) {
			throw new Error('The listener to be removed does not exist');
		}
		listeners.splice(listenerIndex, 1);
		if (listeners.length === 0) {
			element.removeEventListener(
				eventName as string,
				(mapEventNameToSharedListener[
					eventName as string
				] as unknown) as Listener
			);
			delete mapEventNameToListeners[eventName as string];
			delete mapEventNameToSharedListener[eventName as string];
		}
	}

	// Add the `listener` to the list of listeners associated with
	// the `eventName`.
	function add(eventName: string, listener: Listener) {
		let listeners = mapEventNameToListeners[eventName];
		if (!listeners) {
			listeners = [listener];
			mapEventNameToListeners[eventName] = listeners;
			// eslint-disable-next-line no-inner-declarations
			function sharedListener(event: Event | KeyboardEvent) {
				listeners.forEach((listenerFn) => {
					listenerFn(event);
				});
			}
			mapEventNameToSharedListener[eventName] = sharedListener;
			element.addEventListener(eventName, sharedListener);
		} else {
			mapEventNameToListeners[eventName].push(listener);
		}
		return remove.bind(null, eventName, listener);
	}

	return {
		mapEventNameToListeners,
		mapEventNameToSharedListener,
		add,
		remove,
	};
};
